import { supabase } from '@/lib/supabase';

// ── 型別 ────────────────────────────────────────────

export interface AdmScopes {
  is_root: boolean;
  scopes: string[];
  has_rg: boolean;
  has_og: boolean;
}

export interface AdmUserRow {
  user_id: string;
  email: string | null;
  active_scopes: string[];
}

export interface AdmAssignmentRow {
  id: number;
  user_id: string;
  email: string | null;
  scope: string;
  status: string;
  granted_by: string | null;
  granted_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
  notes: string | null;
}

// ── 查詢 ────────────────────────────────────────────

export async function getMyAdmScopes(): Promise<AdmScopes> {
  const { data, error } = await supabase.rpc('get_my_adm_scopes');
  if (error) throw error;
  return (data as AdmScopes) || { is_root: false, scopes: [], has_rg: false, has_og: false };
}

export async function listAdmUsers(): Promise<AdmUserRow[]> {
  const { data, error } = await supabase.rpc('list_adm_users');
  if (error) throw error;
  return (data as AdmUserRow[]) || [];
}

export async function listAdmAssignments(): Promise<AdmAssignmentRow[]> {
  const { data, error } = await supabase.rpc('list_adm_assignments');
  if (error) throw error;
  return (data as AdmAssignmentRow[]) || [];
}

export async function lookupUserByEmail(email: string): Promise<{ user_id: string; email: string } | null> {
  const { data, error } = await supabase.rpc('lookup_user_by_email', { p_email: email });
  if (error) throw error;
  if (!data || (Array.isArray(data) && data.length === 0)) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return row as { user_id: string; email: string };
}

// ── 授權操作 ────────────────────────────────────────

function mapAdmError(error: { message?: string }): Error {
  const raw = error.message || '';
  if (raw.includes('ROLE_FORBIDDEN')) return new Error('僅 ROOT ADM 可執行此操作。');
  if (raw.includes('INVALID_SCOPE')) return new Error('無效的授權範圍。');
  if (raw.includes('USER_NOT_FOUND')) return new Error('找不到該使用者。');
  if (raw.includes('NOT_FOUND')) return new Error('找不到可撤銷的授權紀錄。');
  if (raw.includes('NOT_ACTIVE')) return new Error('該授權目前並非啟用狀態。');
  return new Error(raw || '操作失敗，請稍後再試。');
}

export async function grantAdm(userId: string, scope: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('grant_adm', {
    p_user_id: userId,
    p_scope: scope,
    p_reason: reason || null,
  });
  if (error) throw mapAdmError(error);
}

export async function revokeAdm(userId: string, scope: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_adm', {
    p_user_id: userId,
    p_scope: scope,
    p_reason: reason || null,
  });
  if (error) throw mapAdmError(error);
}

export async function suspendAdm(userId: string, scope: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('suspend_adm', {
    p_user_id: userId,
    p_scope: scope,
    p_reason: reason || null,
  });
  if (error) throw mapAdmError(error);
}